import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Button, Input, Space, message, Spin, Tooltip, Select } from 'antd';
import { 
  ThunderboltOutlined, 
  EditOutlined, 
  FolderOpenOutlined, 
  DownloadOutlined, 
  UploadOutlined,
  SaveOutlined,
  PlusOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { apiURL } from '../../../utils/config';

import CanvasEditor from './CanvasEditor';
import TextControls from './TextControls';
import QuickInsert from './QuickInsert';
import GenerateTab from './GenerateTab';
import TemplateGallery from './TemplateGallery';
import './ImageStudio.css';

const { TabPane } = Tabs;

// Helper to convert base64 data URL to a File object
const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = window.atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

const SIZE_PRESETS = [
  { label: 'Offer Banner (1200x400)', value: 'banner', width: 1200, height: 400 },
  { label: 'Sidebar Patch (304x152)', value: 'patch', width: 304, height: 152 },
  { label: 'Square Grid (512x512)', value: 'square', width: 512, height: 512 },
  { label: 'Sticky Bar (728x90)', value: 'sticky', width: 728, height: 90 },
];

export default function ImageStudio({
  visible,
  onClose,
  onImageSaved,
  contextData = {},
  defaultSize = { width: 1200, height: 400 },
  initialImageUrl,
}) {
  const [activeTab, setActiveTab] = useState('1');
  const [canvas, setCanvas] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [canvasWidth, setCanvasWidth] = useState(defaultSize.width);
  const [canvasHeight, setCanvasHeight] = useState(defaultSize.height);
  const [saving, setSaving] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('banner');
  
  // Template save dialog state
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // Reset states when modal visibility changes
  useEffect(() => {
    if (visible) {
      setSelectedObject(null);
      
      const matchedPreset = SIZE_PRESETS.find(p => p.width === defaultSize.width && p.height === defaultSize.height);
      setSelectedPreset(matchedPreset ? matchedPreset.value : 'banner');

      setCanvasWidth(defaultSize.width);
      setCanvasHeight(defaultSize.height);

      if (initialImageUrl) {
        // If we are editing an existing banner, go straight to Editor tab
        setActiveTab('2');
        const savedDraft = localStorage.getItem('image_studio_draft_' + initialImageUrl);
        if (savedDraft) {
          try {
            const parsed = JSON.parse(savedDraft);
            setBackgroundImage(parsed.backgroundImage || null);
          } catch (e) {
            console.error('Failed to parse draft background:', e);
            setBackgroundImage(initialImageUrl);
          }
        } else {
          setBackgroundImage(initialImageUrl);
        }
      } else {
        setActiveTab('1');
        setBackgroundImage(null);
      }
    }
  }, [visible, initialImageUrl]);

  // Load draft canvas JSON if available when canvas is ready
  useEffect(() => {
    let active = true;
    if (canvas && visible && initialImageUrl) {
      const savedDraft = localStorage.getItem('image_studio_draft_' + initialImageUrl);
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          if (parsed && parsed.canvasJSON) {
            setSaving(true);
            setTimeout(() => {
              if (!active || !canvas.contextContainer) {
                // If component unmounted or canvas was disposed/recreated, abort this load instance safely
                return;
              }
              canvas.loadFromJSON(parsed.canvasJSON, () => {
                canvas.requestRenderAll();
                setSaving(false);
                message.success('Previous edit draft restored!');
              });
            }, 100);
          }
        } catch (e) {
          console.error('Failed to restore canvas draft:', e);
          setSaving(false);
        }
      }
    }
    return () => {
      active = false;
    };
  }, [canvas, visible, initialImageUrl]);

  // Handle preset selector dropdown change
  const handlePresetChange = (value) => {
    setSelectedPreset(value);
    const preset = SIZE_PRESETS.find(p => p.value === value);
    if (!preset) return;

    setCanvasWidth(preset.width);
    setCanvasHeight(preset.height);

    if (canvas) {
      // Set fabric canvas dimension dynamically
      canvas.setDimensions({
        width: preset.width,
        height: preset.height
      });

      // Force background image recalculation and scale if it exists
      if (backgroundImage) {
        const { fabric } = require('fabric');
        fabric.Image.fromURL(
          backgroundImage,
          (img) => {
            if (!img) return;
            const scaleX = preset.width / img.width;
            const scaleY = preset.height / img.height;
            const scale = Math.max(scaleX, scaleY);

            canvas.setBackgroundImage(
              img,
              () => {
                canvas.renderAll();
              },
              {
                scaleX: scale,
                scaleY: scale,
                left: preset.width / 2,
                top: preset.height / 2,
                originX: 'center',
                originY: 'center',
              }
            );
          },
          { crossOrigin: 'anonymous' }
        );
      } else {
        canvas.requestRenderAll();
      }
    }
  };

  // Insert a new interactive text layer into the canvas
  const handleInsertText = (text = 'Double-click to Edit', customOptions = {}) => {
    if (!canvas) {
      message.error('Canvas is not ready yet.');
      return;
    }

    const { fabric } = require('fabric');

    // Calculate a slight offset so multiple text layers don't stack perfectly invisibly on top of each other
    const offset = (canvas.getObjects().length * 20) % 100;

    const itext = new fabric.IText(text, {
      left: (canvas.width / 2) + offset,
      top: (canvas.height / 2) + offset,
      originX: 'center',
      originY: 'center',
      fontFamily: 'Outfit',
      fontSize: 36,
      fill: '#000000',
      textAlign: 'center',
      editable: true,
      ...customOptions,
    });

    canvas.add(itext);
    canvas.setActiveObject(itext);
    canvas.requestRenderAll();
  };

  // Callback when AI generated background is selected
  const handleSelectBackground = (imageSrc, width, height) => {
    setCanvasWidth(width);
    setCanvasHeight(height);
    setBackgroundImage(imageSrc);
    setActiveTab('2'); // Switch to editor
    message.info('Background loaded into editor.');
  };

  // Callback when a template is selected from Gallery
  const handleLoadTemplate = (canvasJSON) => {
    if (!canvas) {
      message.error('Canvas is not initialized.');
      return;
    }

    setSaving(true);
    canvas.loadFromJSON(canvasJSON, () => {
      canvas.requestRenderAll();
      setSaving(false);
      setActiveTab('2'); // Switch to editor
      message.success('Template loaded successfully!');
    });
  };

  // Upload the canvas content to ChoiceDekho media server
  const uploadCanvasImage = async (customFilename = 'canvas_image.png') => {
    if (!canvas) throw new Error('Canvas not initialized');

    // Deselect active object so transformation controls are not baked into the output image
    canvas.discardActiveObject();
    canvas.requestRenderAll();

    const dataUrl = canvas.toDataURL({
      format: 'png',
      quality: 1.0,
      multiplier: 1 / canvas.getZoom(),
    });

    const file = dataURLtoFile(dataUrl, customFilename);
    const formData = new FormData();
    formData.append('image', file);

    const response = await axios.post(`${apiURL}/v1/course/imageUpload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (response.data && response.data.results) {
      return response.data.results;
    } else {
      throw new Error('Image server did not return image URL.');
    }
  };

  // Save canvas as template locally
  const triggerSaveTemplate = () => {
    if (!canvas) return;
    setTemplateName(`Offer Template - ${new Date().toLocaleTimeString()}`);
    setSaveModalVisible(true);
  };

  const submitSaveTemplate = async () => {
    if (!templateName.trim()) {
      message.error('Please enter a template name.');
      return;
    }

    setSaving(true);
    setSaveModalVisible(false);

    try {
      // Get base64 for thumbnail
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      const thumbnailDataUrl = canvas.toDataURL({
        format: 'png',
        quality: 0.4, // lower resolution for thumb
        multiplier: 1 / canvas.getZoom(),
      });

      let finalThumbnailUrl = thumbnailDataUrl;

      // Try uploading thumbnail to the cloud
      try {
        const cloudUrl = await uploadCanvasImage(`thumb_${Date.now()}.png`);
        if (cloudUrl) {
          finalThumbnailUrl = cloudUrl;
        }
      } catch (err) {
        console.warn('Failed to upload thumbnail to cloud. Falling back to local dataURL.', err);
      }

      const newTemplate = {
        id: `tpl_${Date.now()}`,
        name: templateName.trim(),
        thumbnailUrl: finalThumbnailUrl,
        canvasJSON: canvas.toJSON(),
        createdAt: new Date().toISOString(),
      };

      const STORAGE_KEY = 'choicedekho_image_studio_templates';
      const existing = localStorage.getItem(STORAGE_KEY);
      const templates = existing ? JSON.parse(existing) : [];
      templates.unshift(newTemplate);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));

      message.success('Template saved successfully!');
    } catch (err) {
      console.error(err);
      message.error('Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyAndUpload = async () => {
    setSaving(true);
    try {
      const imageUrl = await uploadCanvasImage(`banner_${Date.now()}.png`);
      
      // Save canvas state linked to this cloud URL so it can be restored when editing!
      try {
        localStorage.setItem('image_studio_draft_' + imageUrl, JSON.stringify({
          canvasJSON: canvas.toJSON(),
          backgroundImage: backgroundImage
        }));
      } catch (err) {
        console.warn('Failed to save edit draft in localStorage:', err);
      }

      onImageSaved(imageUrl);
      message.success('Banner compiled and uploaded successfully!');
      onClose();
    } catch (err) {
      console.error(err);
      message.error('Failed to compile or upload the final banner.');
    } finally {
      setSaving(false);
    }
  };

  // Download composed image locally
  const handleDownload = () => {
    if (!canvas) return;

    canvas.discardActiveObject();
    canvas.requestRenderAll();

    const dataUrl = canvas.toDataURL({
      format: 'png',
      quality: 1.0,
      multiplier: 1 / canvas.getZoom(),
    });

    const link = document.createElement('a');
    link.download = `offer_banner_${Date.now()}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('Image download started!');
  };

  return (
    <Modal
      zIndex={2000}
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '96%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '28px',
              height: '28px',
              background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <ThunderboltOutlined style={{ color: 'white', fontSize: '14px' }} />
            </div>
            <span style={{ fontWeight: 600, fontSize: '16px' }}>Image Studio</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '24px' }}>
            <span style={{ fontSize: '12px', fontWeight: 500, color: '#595959' }}>Canvas Preset:</span>
            <Select
              value={selectedPreset}
              onChange={handlePresetChange}
              style={{ width: '200px' }}
              options={SIZE_PRESETS.map(p => ({ label: p.label, value: p.value }))}
              size="small"
            />
          </div>
        </div>
      }
      open={visible}
      onCancel={() => !saving && onClose()}
      footer={
        activeTab === '2' ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Space>
              <Button icon={<SaveOutlined />} onClick={triggerSaveTemplate} disabled={saving}>
                Save for Later
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleDownload} disabled={saving}>
                Download PNG
              </Button>
            </Space>
            <Space>
              <Button onClick={onClose} disabled={saving}>Cancel</Button>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={saving}
                onClick={handleApplyAndUpload}
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
              >
                Apply & Upload
              </Button>
            </Space>
          </div>
        ) : null
      }
      width={1050}
      centered
      destroyOnClose
      maskClosable={false}
      className="studio-modal"
    >
      <Spin spinning={saving} tip="Processing...">
        <Tabs activeKey={activeTab} onChange={setActiveTab} className="image-studio-tabs">
          <TabPane
            tab={<span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><ThunderboltOutlined />1. Generate Background</span>}
            key="1"
          >
            <GenerateTab
              onSelectImage={handleSelectBackground}
              activeWidth={canvasWidth}
              activeHeight={canvasHeight}
            />
          </TabPane>

          <TabPane
            tab={<span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><EditOutlined />2. Studio Editor</span>}
            key="2"
          >
            <div className="editor-tab-container">
              {/* Typography / Style Toolbar + Add Text Action (Above Banner) */}
              <div style={{ background: '#fafafa', border: '1px solid #eaeaea', borderRadius: '8px', padding: '12px', display: 'flex', gap: '24px', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '300px' }}>
                  <TextControls
                    selectedObject={selectedObject}
                    onStyleChange={() => canvas && canvas.requestRenderAll()}
                  />
                </div>
                <div style={{ paddingBottom: '2px' }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleInsertText()}
                    style={{ background: '#1890ff', borderColor: '#1890ff' }}
                  >
                    Add Text Layer
                  </Button>
                </div>
              </div>

              {/* Interactive Fabric Workspace (Full Width) */}
              <div className="editor-workspace" style={{ height: '450px', width: '100%', overflow: 'hidden' }}>
                <CanvasEditor
                  canvasWidth={canvasWidth}
                  canvasHeight={canvasHeight}
                  backgroundImage={backgroundImage}
                  onCanvasReady={setCanvas}
                  onObjectSelected={setSelectedObject}
                />
              </div>
            </div>
          </TabPane>

          <TabPane
            tab={<span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><FolderOpenOutlined />3. Saved Templates</span>}
            key="3"
          >
            <TemplateGallery onLoadTemplate={handleLoadTemplate} />
          </TabPane>
        </Tabs>
      </Spin>

      {/* Save Template Modal Prompt */}
      <Modal
        zIndex={2100}
        title="Save Template Details"
        open={saveModalVisible}
        onOk={submitSaveTemplate}
        onCancel={() => setSaveModalVisible(false)}
        okText="Save"
        destroyOnClose
      >
        <div style={{ padding: '10px 0' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Template Name</label>
          <Input
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g. 50% discount coupon banner"
          />
        </div>
      </Modal>
    </Modal>
  );
}
